// ChatApp.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Add as AddIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const TherapistChatPage = () => {
  // --- mock data ---
  const mockChats = [
    {
      id: '1',
      name: 'Jane Cooper',
      avatar:
        'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&h=256&auto=format&fit=crop',
      status: 'online',
      lastMessage: {
        content: "Sure, I'll send you the files later today",
        timestamp: new Date(Date.now() - 1000 * 60 * 10),
        isRead: false,
      },
      unreadCount: 2,
    },
    {
      id: '2',
      name: 'Alex Morgan',
      avatar:
        'https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=256&h=256&auto=format&fit=crop',
      status: 'offline',
      lastMessage: {
        content: "How's the new design coming along?",
        timestamp: new Date(Date.now() - 1000 * 60 * 30),
        isRead: true,
      },
    },
    {
      id: '3',
      name: 'Leslie Alexander',
      avatar:
        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=256&h=256&auto=format&fit=crop',
      status: 'away',
      lastMessage: {
        content: 'Looking forward to our meeting tomorrow',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
        isRead: true,
      },
    },
  ];

  const mockMessagesByChatId = {
    '1': [
      {
        id: '1',
        content: 'Hi there! How are you doing today?',
        sender: {
          id: '2',
          name: 'Jane Cooper',
          avatar:
            'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&h=256&auto=format&fit=crop',
          isCurrentUser: false,
        },
        timestamp: new Date(Date.now() - 1000 * 60 * 60),
        status: 'read',
      },
      {
        id: '2',
        content: "I'm doing well, thanks for asking! Just working on the new project design.",
        sender: { id: '1', name: 'You', isCurrentUser: true },
        timestamp: new Date(Date.now() - 1000 * 60 * 55),
        status: 'read',
      },
      {
        id: '3',
        content: 'That sounds great! Could you share some of your progress with me?',
        sender: {
          id: '2',
          name: 'Jane Cooper',
          avatar:
            'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&h=256&auto=format&fit=crop',
          isCurrentUser: false,
        },
        timestamp: new Date(Date.now() - 1000 * 60 * 50),
        status: 'read',
      },
      {
        id: '4',
        content: "Sure, I'll send you the files later today",
        sender: { id: '1', name: 'You', isCurrentUser: true },
        timestamp: new Date(Date.now() - 1000 * 60 * 10),
        status: 'delivered',
      },
    ],
    '2': [
      {
        id: '1',
        content: "How's the new design coming along?",
        sender: {
          id: '2',
          name: 'Alex Morgan',
          avatar:
            'https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=256&h=256&auto=format&fit=crop',
          isCurrentUser: false,
        },
        timestamp: new Date(Date.now() - 1000 * 60 * 30),
        status: 'read',
      },
      {
        id: '2',
        content: "It's going well! I should be done by tomorrow.",
        sender: { id: '1', name: 'You', isCurrentUser: true },
        timestamp: new Date(Date.now() - 1000 * 60 * 25),
        status: 'read',
      },
    ],
  };

  // --- component state ---
  const [search, setSearch] = useState('');
  const [selectedChatId, setSelectedChatId] = useState(mockChats[0].id);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  const messagesEndRef = useRef(null);

  // load messages on chat change
  useEffect(() => {
    setMessages(mockMessagesByChatId[selectedChatId] || []);
  }, [selectedChatId]);

  // scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // send a message
  const handleSend = () => {
    if (!newMessage.trim()) return;
    const msg = {
      id: Date.now().toString(),
      content: newMessage,
      sender: { id: '1', name: 'You', isCurrentUser: true },
      timestamp: new Date(),
      status: 'sending',
    };
    setMessages((m) => [...m, msg]);
    setNewMessage('');
    // simulate send → sent → delivered
    setTimeout(() => {
      setMessages((m) =>
        m.map((x) => (x.id === msg.id ? { ...x, status: 'sent' } : x))
      );
      setTimeout(() => {
        setMessages((m) =>
          m.map((x) => (x.id === msg.id ? { ...x, status: 'delivered' } : x))
        );
      }, 500);
    }, 300);
  };

  // filter chats by search
  const filteredChats = mockChats.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Grid container sx={{ height: '100vh' }}>
      {/* Left pane */}
      <Grid
        item
        xs={4}
        sx={{
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">Messages</Typography>
          <Box>
            <IconButton size="small">
              <MenuIcon />
            </IconButton>
            <IconButton size="small">
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
        <Divider />

        {/* search */}
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search messages"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />

        {/* chat list */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <List disablePadding>
            {filteredChats.map((chat) => (
              <ListItem
                button
                key={chat.id}
                selected={chat.id === selectedChatId}
                onClick={() => setSelectedChatId(chat.id)}
                alignItems="flex-start"
              >
                <ListItemAvatar>
                  <Avatar src={chat.avatar} alt={chat.name}>
                    {chat.name.slice(0, 2)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography noWrap>{chat.name}</Typography>
                      {chat.unreadCount > 0 && (
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            borderRadius: 2,
                            px: 1,
                            fontSize: 12,
                          }}
                        >
                          {chat.unreadCount}
                        </Box>
                      )}
                    </Box>
                  }
                  secondary={
                    chat.lastMessage && (
                      <Typography
                        noWrap
                        variant="body2"
                        color={chat.lastMessage.isRead ? 'text.secondary' : 'text.primary'}
                      >
                        {chat.lastMessage.content}
                      </Typography>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Grid>

      {/* Right pane */}
      <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* chat header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Avatar
            src={mockChats.find((c) => c.id === selectedChatId)?.avatar}
            sx={{ mr: 2 }}
          >
            {mockChats.find((c) => c.id === selectedChatId)?.name.slice(0, 2)}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1">
              {mockChats.find((c) => c.id === selectedChatId)?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {mockChats.find((c) => c.id === selectedChatId)?.status ===
              'online'
                ? 'Online now'
                : mockChats.find((c) => c.id === selectedChatId)?.status ===
                  'away'
                ? 'Away'
                : 'Offline'}
            </Typography>
          </Box>
          <IconButton>
            <PhoneIcon />
          </IconButton>
          <IconButton>
            <VideocamIcon />
          </IconButton>
        </Box>

        {/* messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'background.default' }}>
          {messages.map((msg) => {
            const isSent = msg.sender.isCurrentUser;
            return (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: isSent ? 'flex-end' : 'flex-start',
                  mb: 1.5,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    maxWidth: '70%',
                    bgcolor: isSent ? 'primary.main' : 'grey.100',
                    color: isSent ? 'primary.contrastText' : 'text.primary',
                    borderRadius: isSent
                      ? '16px 0 16px 16px'
                      : '0 16px 16px 16px',
                  }}
                >
                  <Typography variant="body2">{msg.content}</Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.5, textAlign: isSent ? 'right' : 'left' }}
                    color="text.secondary"
                  >
                    {formatDistanceToNow(msg.timestamp, { addSuffix: true })}
                    {isSent && (msg.status === 'read' ? ' ✓✓' : msg.status === 'delivered' ? ' ✓✓' : msg.status === 'sent' ? ' ✓' : '…')}
                  </Typography>
                </Paper>
              </Box>
            );
          })}
          <div ref={messagesEndRef} />
        </Box>

        {/* input */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <IconButton>
            <AttachFileIcon />
          </IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            variant="outlined"
          />
          <IconButton>
            <EmojiEmotionsIcon />
          </IconButton>
          <IconButton color="primary" onClick={handleSend}>
            <SendIcon />
          </IconButton>
        </Box>
      </Grid>
    </Grid>
  );
};

export default TherapistChatPage;
